/**
 * Time <-> frame math for the Frame picker (FRAME_PICKER_DESIGN.md §3.2, §6.2 `media/`, §6.3).
 *
 * React-free and pure: every function takes a `Timeline` and numbers and returns numbers or
 * strings. Nothing here reads the DOM, the clock or the probe directly.
 *
 * Design §6.2: "`timeline.ts` (time↔frame math, VFR-safe; total frames = `round(duration × fps)`,
 * `numberOfFrames` from the probe is never used)".
 *
 * R14 (slice L, BUG-016, R14-SRV5): the probe now also says how many frames are really there (`frame_count`), the
 * timestamp-derived rate (`avg_framerate`) and whether the rate is constant (`frame_rate_mode`). `createTimeline` takes
 * those as `ProbeFacts`: under CFR the probe's count wins over `round(duration × fps)` when the two are within
 * `PROBE_COUNT_SLACK_FRAMES`; under VFR the timeline is `variable` -- the average rate, the real count, and NO frame grid
 * (`snapToFrame` only clamps, `stepFrames` moves by the average frame, `seekTargetFor` nudges half a millisecond,
 * `frameSeekTime` writes the instant floored to the millisecond). `frameIndex` / `frameNumber` keep the average-grid
 * arithmetic; nothing user-facing reads them under `variable`.
 */

export interface Timeline {
    /** Clip length in seconds, from the edge's probe. */
    duration: number;
    /** Nominal frames per second, from the probe (`videoRFrameRage`); `ASSUMED_FPS` when unknown. */
    fps: number;
    /** Design §6.2: "total frames = `round(duration × fps)`". */
    frameCount: number;
    /**
     * Round 11 (K, R10 N1): a STAND-IN built from the stage element's own `duration` (`ASSUMED_FPS`) while the
     * upload runs and the edge has not described the clip yet -- the keys, the strip and the Output work off the
     * local file from the first presented frame instead of after the upload (8–27 s). The counter and the header
     * say the length but not a frame rate they do not know; the edge's answer replaces it.
     */
    provisional?: boolean;
    /**
     * R14 (slice L, BUG-016): the probe said `frame_rate_mode: 'VFR'` -- `fps` is the AVERAGE rate, `frameCount` the frames
     * really present, and there is no frame grid: labels are time-only, the step keys move by the average frame. Set ONLY
     * under VFR (absent, not `false`, otherwise) so a CFR timeline is exactly `{ duration, fps, frameCount }`.
     */
    variable?: boolean;
}

/**
 * R14 (slice L): what the probe knows beyond `duration` and the nominal rate (R14_SRV5_NOTES §1.2, all optional): the frames
 * actually present (`frame_count`), the timestamp-derived rate (`avg_framerate`) and the server's CFR / VFR verdict
 * (`frame_rate_mode`; its 1 % rule -- the client never re-derives it). An older edge omits them all.
 */
export interface ProbeFacts {
    frameCount?: number | null;
    avgFps?: number | null;
    mode?: 'CFR' | 'VFR' | null;
}

/**
 * R14 (slice L): under CFR the probe's `frame_count` replaces `round(duration × fps)` when the two differ by at most this many
 * frames -- container durations lie by a frame or two (the anamorphic `.mpg`: 1188 counted vs 1190 computed → 1188). A larger
 * gap keeps the computed count: the clip is not what the CFR verdict says (D under a forced CFR: 240 vs 364 → 364).
 */
export const PROBE_COUNT_SLACK_FRAMES = 2;

/**
 * R14 (slice L): where the player seeks under `variable` -- the playhead plus half a millisecond, so a frame whose pts is
 * exactly the playhead is not shown as its predecessor by an engine's µs / tick truncation (the CFR midpoint rule needs a
 * grid; a VFR clip has none).
 */
export const VFR_SEEK_NUDGE_S = 5e-4;

/** Ironman `FramePickerRequest.ASSUMED_FPS`: "When the probe cannot report a frame rate, §3.2 says assume 30 fps." */
export const ASSUMED_FPS = 30;

/**
 * Design §3.2: "`duration − 1/fps < t ≤ duration + 0.5 s` → clamped to the last frame (container
 * durations lie by a few frames; VFR tails)". Mirrors ironman `END_CLAMP_TOLERANCE_SECONDS`.
 */
export const END_CLAMP_TOLERANCE_SECONDS = 0.5;

/**
 * Build a `Timeline` from the probe's duration and frame rate. A non-finite or non-positive fps
 * falls back to `ASSUMED_FPS` exactly as ironman's `parse` does; a negative duration is treated as
 * 0 (`coerceAtLeast(0.0)`).
 */
export function createTimeline(duration: number, fps?: number, facts?: ProbeFacts | null): Timeline {
    const d = Number.isFinite(duration) && duration > 0 ? duration : 0;
    const nominal = typeof fps === 'number' && Number.isFinite(fps) && fps > 0 ? fps : ASSUMED_FPS;
    const counted = positiveOrNull(facts?.frameCount);
    if (facts?.mode === 'VFR') {
        // R14 (slice L, BUG-016): the average rate, else the count over the length, else the nominal rate; the real count,
        // else the estimate at that rate. `lastFrameTime` = `(N − 1) / avg` falls out (D: 239 / 19.78 = 12.083 = duration − 1/avg).
        const avg = positiveOrNull(facts.avgFps) ?? (counted !== null && d > 0 ? counted / d : nominal);
        const frames = counted !== null ? Math.round(counted) : Math.round(d * avg);
        return { duration: d, fps: avg, frameCount: frames, variable: true };
    }
    const computed = Math.round(d * nominal);
    // Review 7 P1-06 / P1-09: under the edge's CFR verdict (its timestamps agree with the rate) FEWER frames than the container's
    // length implies means the container outlasts the video -- a longer audio track (the Dzien Dobry MOV: 901 frames, "916"
    // computed), a stream starting late (oracle-avoffset: 237, "240"). The frames present are the truth: the counter said
    // "of 240" and the last frames it promised did not exist (the end stopped at 237). More frames than computed keeps the slack rule.
    const fewerUnderCfr = facts?.mode === 'CFR' && counted !== null && Math.round(counted) < computed;
    const frames = counted !== null && (fewerUnderCfr || Math.abs(Math.round(counted) - computed) <= PROBE_COUNT_SLACK_FRAMES) ? Math.round(counted) : computed;
    return { duration: d, fps: nominal, frameCount: frames };
}

function positiveOrNull(x: number | null | undefined): number | null {
    return typeof x === 'number' && Number.isFinite(x) && x > 0 ? x : null;
}

/** Seconds per frame, `1/fps` (design §6.3: "Frame stepping is by timestamp: `1/fps` nominal from the probe"). */
export function frameDuration(tl: Timeline): number {
    return 1 / tl.fps;
}

/**
 * Timestamp of the last frame: the START of frame `frameCount − 1` on the nominal grid, `(frameCount − 1) / fps`, never
 * below 0. R14 (Sr review 1, MUST-FIX 1): this was ironman's `duration − 1/fps`, which under the CONTAINING rule
 * (`frameIndex`) left the last frame unreachable whenever `frac(duration × fps) ∈ [0.5, 1)` -- 8 s @ 29.97 stopped at 238
 * of "240", 8.02 s @ 30 at 239 of "241" -- while the counter said "of N" and the hint taught `Nf`. The picker's own grid
 * (`frameCount = round(duration × fps)`, the counter's N) decides where the last frame is; the wire for it,
 * `floorMs((N − 1.5) / fps)`, is ≤ `duration − 1/fps` for every `frac` (N ≤ duration × fps + 0.5), so it stays inside
 * ironman's in-range band. `timeBand`'s tail band still runs to `duration + 0.5 s`.
 */
export function lastFrameTime(tl: Timeline): number {
    return Math.max(0, (tl.frameCount - 1) / tl.fps);
}

/**
 * Which §3.2 band a one-frame time falls in. The client never sends an out-of-range time (see
 * `clampToRange`), but the UI needs the band to show the §3.5 copy when the user types one.
 *
 * Design §3.2: "`t < 0` or not a number → `TIME_OUT_OF_RANGE`; `0 ≤ t ≤ duration − 1/fps` → as is;
 * `duration − 1/fps < t ≤ duration + 0.5 s` → clamped to the last frame; `t > duration + 0.5 s` →
 * `TIME_OUT_OF_RANGE`". R14: the in-range bound is the picker's `lastFrameTime` (the last frame's START on the
 * grid); the tail band's end is unchanged.
 */
export type TimeBand = 'invalid' | 'in-range' | 'tail-clamp' | 'past-end';

export function timeBand(t: number, tl: Timeline): TimeBand {
    if (typeof t !== 'number' || Number.isNaN(t) || t < 0) { return 'invalid'; }
    if (t <= lastFrameTime(tl)) { return 'in-range'; }
    if (t <= tl.duration + END_CLAMP_TOLERANCE_SECONDS) { return 'tail-clamp'; }
    return 'past-end';
}

/**
 * Client-side mirror of the §3.2 bands as a total function: the playhead can never hold a time
 * the edge would refuse, so the two refusal bands collapse onto the nearest valid frame.
 *
 * - `invalid` (NaN or `t < 0`) → 0, the first frame.
 * - `in-range` → `t` unchanged ("as is").
 * - `tail-clamp` → `lastFrameTime` ("clamped to the last frame").
 * - `past-end` → also the last frame (the edge would refuse; the client clamps and lets
 *   `timeBand` drive the copy).
 */
export function clampToRange(t: number, tl: Timeline): number {
    switch (timeBand(t, tl)) {
        case 'invalid': return 0;
        case 'in-range': return t;
        default: return lastFrameTime(tl);
    }
}

/**
 * R14 (BUG-001 / BUG-011): the CONTAINING frame's tolerance, in FRAMES -- a thousandth of a frame (33 µs at 30 fps,
 * 8 µs at 120 fps). It absorbs the µs truncation of an element-reported `currentTime` / `mediaTime` (a frame's pts
 * `1.4333333` read back as `1.433333`) and float error on `k/fps × fps`, and never crosses into the next frame.
 */
export const FRAME_EPS = 1e-3;

/**
 * R14 (BUG-011): what a TYPED time means. A user types at millisecond precision (the field shows `0:01.433` for
 * frame 43's `1.43333…`; the QA oracle typed `1.4333`), so a typed value up to half a millisecond BELOW a frame's
 * start names that frame -- typing back what the field shows must land on the frame it showed. The transport adds
 * this before `snapToFrame`; nothing else does (element-reported times are exact to the µs and use `FRAME_EPS`).
 */
export const TYPED_TIME_SLACK_S = 5e-4;

/**
 * Snap a time onto the nominal frame grid: the START of the frame CONTAINING `t` (`frameIndex / fps`), clamped into
 * the valid band. R14 (BUG-001, BUG-011): this used to be `round(t × fps) / fps` -- a typed time in the upper half
 * of a frame's span moved to the NEXT frame (the frame a player shows when paused there is the containing one).
 * Design §6.3: "when `requestVideoFrameCallback` reports a `mediaTime` the display snaps to it" — that snap is the
 * caller's; this one is the nominal grid used when no presented time is known.
 */
export function snapToFrame(t: number, tl: Timeline): number {
    // R14 (slice L, BUG-016): a variable-rate clip has no grid to snap onto -- the instant itself, clamped into the band.
    if (tl.variable) return clampToRange(t, tl);
    return frameStart(frameIndex(t, tl), tl);
}

/**
 * Review 7 P1-07: the frame a PRESENTED timestamp belongs to. A frame's pts sits ON the grid up to its container's timebase
 * rounding -- a WEBM stores milliseconds, so frame 151 at 30 fps is 5.033, not 5.0333…, and the containing rule
 * (`snapToFrame`, 1e-3 frame of slack) named it frame 150: after one step the counter said Frame 151 while the picture, the
 * name and the export were 152. A pts is a frame START give or take its timebase, so the containing rule gets
 * `PTS_SLACK_FRAMES` of slack -- enough for a millisecond timebase at 120 fps (0.06 frame), far less than a real offset a
 * frame start can sit at when the body's first-frame origin is not known (0.3 frame on the R14 offset fixture, still
 * frame k). Variable rate: the pts itself.
 */
export const PTS_SLACK_FRAMES = 0.07;
export function snapPtsToFrame(pts: number, tl: Timeline): number {
    if (tl.variable) return clampToRange(pts, tl);
    const k = Math.floor(clampToRange(pts, tl) * tl.fps + PTS_SLACK_FRAMES);
    return frameStart(Math.min(Math.max(0, k), Math.max(0, tl.frameCount - 1)), tl);
}

/**
 * 0-based index of the frame CONTAINING `t` after clamping: `floor(clamp(t) × fps + FRAME_EPS)`, never past the
 * last frame. R14 (BUG-001): was `round(t × fps)`, which named frame k for a time inside frame k − 1.
 */
export function frameIndex(t: number, tl: Timeline): number {
    const k = Math.floor(clampToRange(t, tl) * tl.fps + FRAME_EPS);
    const last = Math.max(0, tl.frameCount - 1);
    return Math.max(0, Math.min(k, last));
}

/**
 * WP-02 (whole-wave review): THE wire -> frame rule, one copy. The 0-based frame the edge extracts for a wire instant -- ironman
 * `FramePickerRequest.frameIndexAt`, `ceil(t·fps − 1e-3)`: `-ss t` delivers the first frame with pts ≥ t, and the picker writes at
 * or before the frame's pts and after the previous frame's (`frameSeekTime`), so the ceiling names the frame the user saw. Clamped
 * into the grid. Read by the picker's "saves as" name, the restore (`root/rehydrate.ts#userTimeFromSeek`) and the /downloads card
 * (`ui/file-list/frame-picker-card.ts#frameIndexFromWire`).
 */
export function frameFromWire(wireT: number, tl: Timeline): number {
    const k = Math.ceil(wireT * tl.fps - 1e-3);
    return Math.max(0, Math.min(Number.isFinite(k) ? k : 0, Math.max(0, tl.frameCount - 1)));
}

/** R14: the exact start of frame `k` (0-based), `k / fps`, clamped into the valid band. */
export function frameStart(k: number, tl: Timeline): number {
    const n = Number.isFinite(k) ? Math.max(0, Math.floor(k)) : 0;
    return clampToRange(n / tl.fps, tl);
}

/**
 * R14 (BUG-001, spec 3): where the PLAYER seeks the element for the playhead `t` -- the MIDPOINT of the frame
 * containing `t`, `(k + 0.5) / fps`, so no engine's µs / tick truncation of `k / fps` can land inside frame k − 1
 * (Chromium set to `1.433` showed frame 42 for a playhead naming 43). Without a timeline: `t` itself. Never past the
 * clip's end. The element's REPORTED time (rVFC `mediaTime`, `currentTime`) maps back to k by the containing rule.
 */
export function seekTargetFor(t: number, tl: Timeline | null): number {
    // R3-JC-02: a PROVISIONAL timeline's rate is a guess (`ASSUMED_FPS`) -- its "midpoint" put 1:01:30.560 of a 25 fps clip at
    // 3690.55 (frame .520 on screen), and that picture then named the job. No grid is known yet: the instant itself.
    if (!tl || !(tl.fps > 0) || tl.provisional) return Math.max(0, Number.isFinite(t) ? t : 0);
    if (tl.variable) {
        // R14 (slice L, BUG-016): no grid, no midpoint -- the clamped playhead plus `VFR_SEEK_NUDGE_S`, never past the end.
        const nudged = clampToRange(t, tl) + VFR_SEEK_NUDGE_S;
        return tl.duration > 0 ? Math.min(nudged, tl.duration) : nudged;
    }
    const mid = (frameIndex(t, tl) + 0.5) / tl.fps;
    return tl.duration > 0 ? Math.min(mid, tl.duration) : mid;
}

/**
 * The 1-based frame number shown in the chip: the containing frame's index + 1 (design §6.3: "1-based of
 * `round(duration × fps)`"; R14: containing, not nearest).
 */
export function frameNumber(t: number, tl: Timeline): number {
    return frameIndex(t, tl) + 1;
}

/**
 * IT1 (W3, qa #2 -- "delivered frame == previewed frame"). The instant a ONE-frame job asks ffmpeg for. ironman runs
 * `-ss <t>` as an INPUT option (`FrameExtraction.buildOneFrameCommand`), which delivers the FIRST frame with pts ≥ t
 * (measured 2026-09-21 on `15_second_video.mp4`, first pts 0.066: `-ss 7.267` → 7.299, `-ss 7.266` → 7.266, `-ss 1.567`
 * → 1.599). The element shows the frame with pts ≤ its time. So the write must be ≤ the PRESENTED frame's pts and >
 * the previous frame's: the presented pts itself (rVFC `mediaTime`, the player's `onPresentedAt`) FLOORED to the
 * millisecond ironman keeps (`fmt` rounds: 7.26667 → 7.267 would be the next frame again) -- but ONLY when it is
 * earlier than `t`: Chromium's `mediaTime` is the frame's pts (7.266 for 7.267), Firefox's echoes the requested
 * time (7.267, measured 2026-09-21 on dev), and a pts at or after `t` says nothing the nominal rule does not.
 * Otherwise the nominal frame's start minus half a frame -- robust to the 3-decimal rounding on a clip whose pts
 * start at 0, and on the offset clip it delivers the shown frame too (7.267 → 7.25 → 7.266; 1.567 → 1.55 → 1.566;
 * 0 → 0 → 0.066, all verified with `ffmpeg -copyts -ss`). Never below 0.
 */
export function frameSeekTime(t: number, tl: Timeline | null, presentedPts?: number | null): number {
    if (typeof presentedPts === 'number' && Number.isFinite(presentedPts) && presentedPts >= 0 && presentedPts < t - 1e-4) return floorMs(presentedPts);
    if (!tl || !(tl.fps > 0)) return floorMs(Math.max(0, t));
    // R14 (slice L, BUG-016): under `variable` there is no grid and so no half-frame rule -- the instant floored to the ms
    // (the presented-pts rule above still applies: delivered == previewed, as today).
    if (tl.variable) return floorMs(Math.max(0, t));
    const n = frameIndex(t, tl);
    return Math.max(0, floorMs((n - 0.5) / tl.fps));
}

/** Seconds floored to the millisecond (`1e-6` absorbs `x.xxx` read back as `x.xxx - ulp`). */
export function floorMs(t: number): number {
    return Math.floor(t * 1000 + 1e-6) / 1000;
}

/** Time of the frame `n` frames away from `t` (negative `n` steps back), clamped. */
export function stepFrames(t: number, n: number, tl: Timeline): number {
    // R14 (slice L, BUG-016): under `variable` a step is the AVERAGE frame from where the playhead is, not a grid index.
    if (tl.variable) return clampToRange(t + n / tl.fps, tl);
    return clampToRange((frameIndex(t, tl) + n) / tl.fps, tl);
}

/**
 * The transport's "bigger step". Design §6.2 `transport.component.tsx`: "back/forward a bigger step
 * (¼ s ≤ 10 s, 1 s above)"; §6.6 keyboard model: "the bigger step (¼ s on clips ≤ 10 s … 1 s above)".
 */
export function bigStepSeconds(tl: Timeline): number {
    return tl.duration <= 10 ? 0.25 : 1;
}

/**
 * The duration-adaptive tile spacing. Design §6.3 step 4: "every frame for clips ≤ 5 s (the
 * `strip-dense` case), every 0.25 s up to 30 s, every second up to 2 min, every 5 s beyond".
 */
export function tileStepSeconds(tl: Timeline): number {
    if (tl.duration <= 5) { return frameDuration(tl); }
    if (tl.duration <= 30) { return 0.25; }
    if (tl.duration <= 120) { return 1; }
    return 5;
}

/**
 * Tile timestamps for the whole clip on the §6.3 ladder: `0, step, 2·step, …` while `≤ lastFrame`.
 * Always includes 0 so an empty or near-empty clip still yields one tile.
 *
 * The ROOT still uses this ladder as its sharpness/tile tolerance. The filmstrip's "Whole clip"
 * layout no longer does (picker-fix Q5): it is width-aware, see `tileLayout`.
 */
export function tileTimes(tl: Timeline): number[] {
    const step = tileStepSeconds(tl);
    const last = lastFrameTime(tl);
    const out: number[] = [];
    // Multiply, never accumulate: `k * step` does not drift the way `t += step` does. The
    // half-microsecond tolerance absorbs the one-ulp error of e.g. `19 * 0.1` against `2 − 0.1`.
    const eps = 5e-7;
    for (let k = 0; ; k++) {
        const t = k * step;
        if (t > last + eps && k > 0) { break; }
        out.push(t);
        if (last <= 0) { break; }
    }
    return out;
}

/** The "Whole clip" strip's geometry: `count` tiles of `tileWidth` px filling `width` exactly. */
export interface TileLayout {
    count: number;
    tileWidth: number;
    /** One time per tile: `k · duration / count`, snapped to the frame grid and clamped. */
    times: number[];
}

/**
 * How many tiles fit a strip (picker-fix brief F3): `n = floor(width / minTile)`, at least one,
 * never more than the clip has frames. The tiles then stretch to `width / n`, so with
 * `minTile` 72 a desktop tile is 72–96 px and a 56 px phone tile 56–74 px — never a sliver, and
 * the strip never scrolls or exceeds its column.
 */
export function tileCount(width: number, minTile: number, frameCount: number): number {
    const w = Number.isFinite(width) && width > 0 ? width : 0;
    const min = Number.isFinite(minTile) && minTile > 0 ? minTile : 1;
    const frames = Math.max(1, Math.floor(Number.isFinite(frameCount) ? frameCount : 1));
    return Math.max(1, Math.min(frames, Math.floor(w / min) || 1));
}

/**
 * `count` times spread evenly over the clip — `k · duration / count`, each snapped to its frame
 * and clamped into the valid band — so tile `k` shows the first frame of its `1/count` slice.
 */
export function spreadTimes(tl: Timeline, count: number): number[] {
    const n = Math.max(1, Math.floor(count));
    const out: number[] = [];
    for (let k = 0; k < n; k++) out.push(snapToFrame((k * tl.duration) / n, tl));
    return out;
}

export function tileLayout(tl: Timeline, width: number, minTile: number, maxCount?: number): TileLayout {
    // Round 5 (P4): a strip drawn from the server's sprite has as many moments as the sprite has
    // cells; more tiles than that would repeat a cell under a different time label.
    const cap = Number.isFinite(maxCount) && (maxCount as number) > 0 ? Math.floor(maxCount as number) : Infinity;
    const count = Math.min(tileCount(width, minTile, tl.frameCount), cap);
    const w = Number.isFinite(width) && width > 0 ? width : minTile * count;
    return { count, tileWidth: w / count, times: spreadTimes(tl, count) };
}

/**
 * The picker's timecode — "m:ss.mmm", hours only when ≥ 1 h (post-wave ruling 1):
 * `formatHms(1.567) === '0:01.567'`, `formatHms(3723.456) === '1:02:03.456'`. Milliseconds are
 * rounded (not truncated) so a presented `mediaTime` like 1.5669999 reads as the frame it is.
 * Negative or non-finite input renders as 0.
 */
export function formatHms(t: number): string {
    const { h, m, s, ms } = hmsParts(t);
    const mm = h > 0 ? pad(m, 2) : String(m);
    return `${h > 0 ? `${h}:` : ''}${mm}:${pad(s, 2)}.${pad(ms, 3)}`;
}

/**
 * The always-hours form, "h:mm:ss.mmm" (`formatHmsLong(1.567) === '0:00:01.567'`) — the brief
 * Appendix shape, kept for a caller that needs a fixed-width column. Nothing in the picker's
 * UI uses it by default.
 */
export function formatHmsLong(t: number): string {
    const { h, m, s, ms } = hmsParts(t);
    return `${h}:${pad(m, 2)}:${pad(s, 2)}.${pad(ms, 3)}`;
}

function hmsParts(t: number): { h: number; m: number; s: number; ms: number } {
    const total = Number.isFinite(t) && t > 0 ? t : 0;
    let ms = Math.round(total * 1000);
    const h = Math.floor(ms / 3_600_000);
    ms -= h * 3_600_000;
    const m = Math.floor(ms / 60_000);
    ms -= m * 60_000;
    const s = Math.floor(ms / 1000);
    ms -= s * 1000;
    return { h, m, s, ms };
}

/**
 * "m:ss" — the shape ironman's §3.5 messages use for durations
 * (`FramePickerRequest.formatDuration`: `65 s → "1:05"`, whole seconds, truncated).
 */
export function formatMinSec(t: number): string {
    const whole = Math.max(0, Math.trunc(Number.isFinite(t) ? t : 0));
    return `${Math.floor(whole / 60)}:${pad(whole % 60, 2)}`;
}

function pad(n: number, width: number): string {
    return String(n).padStart(width, '0');
}
